import type { Edge, Node } from '@xyflow/react';

import type { FlatGraph } from '@/sections/insights/queryBuilder/types/FlatGraph';

/**
 * Pure function to transform FlatGraph to ReactFlow format
 *
 * @param flatGraph - FlatGraph from QueryEditorContext
 * @returns Transformed nodes and edges (positions from flatGraph.uiState)
 */
export function transformToReactFlow(flatGraph: FlatGraph | undefined): {
  nodes: Node[];
  edges: Edge[];
} {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Convert FlatGraph nodes to ReactFlow nodes
  const flatGraphNodes = flatGraph?.nodes.entries() || [];
  for (const [nodeId, flatNode] of flatGraphNodes) {
    nodes.push({
      id: nodeId, // Use FlatGraph UUID directly (stable identity)
      type: 'entity',
      data: {
        labels: flatNode.data.labels || [],
        filters: flatNode.data.filters || [],
        nodeData: flatNode.data, // Native FlatGraphNode['data'] for modal
      },
      position: flatNode.uiState?.position || { x: 0, y: 0 },
    });
  }

  // Convert FlatGraph edges to ReactFlow edges
  const flatGraphEdges = flatGraph?.edges.entries() || [];
  for (const [edgeId, flatEdge] of flatGraphEdges) {
    edges.push({
      id: edgeId, // Use FlatGraph UUID directly (stable identity)
      source: flatEdge.sourceId,
      target: flatEdge.targetId,
      type: 'relationship',
      data: {
        label: flatEdge.data.label,
        edgeData: flatEdge.data, // Native FlatGraphEdge['data'] for modal
      },
    });
  }

  return { nodes, edges };
}

/**
 * Pure function to hydrate nodes/edges with editable props and modal state
 *
 * @param nodes - Nodes to hydrate
 * @param edges - Edges to hydrate
 * @param editable - Whether nodes/edges should be editable
 * @param expandedModalIds - Set of IDs for nodes/edges with expanded modals
 * @param onToggleModal - Callback to toggle modal expansion
 * @param rootNodeId - Optional ID of the root node (to mark it visually)
 * @returns Hydrated nodes and edges
 */
export function hydrateReactFlowElements(
  nodes: Node[],
  edges: Edge[],
  editable: boolean,
  expandedModalIds: Set<string>,
  onToggleModal: (id: string) => void,
  rootNodeId?: string
): { nodes: Node[]; edges: Edge[] } {
  const hydratedNodes = nodes.map(node => ({
    ...node,
    data: {
      ...node.data,
      editable,
      isExpanded: expandedModalIds.has(node.id),
      onToggleModal: () => onToggleModal(node.id),
      isRootNode: rootNodeId ? node.id === rootNodeId : false,
    },
  }));

  const hydratedEdges = edges.map(edge => ({
    ...edge,
    data: {
      ...edge.data,
      editable,
      isExpanded: expandedModalIds.has(edge.id),
      onToggleModal: () => onToggleModal(edge.id),
    },
  }));

  return { nodes: hydratedNodes, edges: hydratedEdges };
}
